import type { SummaryReportGroup } from 'api/types/reports.types';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { exportToCSV } from 'shared/utils/exportUtils';
import {
  COLOR,
  COL_PCT_X,
  MARGIN_L,
  TEXT,
  applyStyle,
  drawDonutWithLegend,
  drawHeader,
  drawProjectBarChart,
  drawRule,
  drawStats,
  drawText,
  drawTextRight,
  drawTreeRow,
  ensureSpace,
  treeRowHeight,
} from 'shared/utils/pdfBuilder';
import type {
  BarChartProject,
  BarChartSlice,
  DonutSliceDef,
  TextStyle,
} from 'shared/utils/pdfBuilder';
import * as XLSX from 'xlsx-js-style';

import { toHHMM } from '../../timer/utils/trackerUtils';

import { formatAmount, formatDuration, resolveGroupLabel } from './reportsUtils';

// Amount as plain number string (currency shown only in column header)
const formatAmountValue = (cents: number): string => (cents / 100).toFixed(2);

export interface DetailedEntry {
  id: string;
  userId: string;
  projectId?: string;
  clientId?: string;
  description?: string;
  isBillable: boolean;
  date: string;
  startedAt: string;
  endedAt?: string;
  durationSecs: number;
  amountCents: number;
  userName?: string;
  userEmail?: string;
  projectName?: string;
  projectColor?: string;
  clientName?: string;
}

export interface SummaryRow {
  id: string;
  projectId?: string;
  clientId?: string;
  userId?: string;
  /** The raw key value for the top-level group entity (same as projectId/clientId/userId but always set when groupField is a linkable type) */
  entityKey?: string;
  projectName: string;
  clientName: string;
  color: string;
  totalDurationSecs: number;
  billableDurationSecs: number;
  amountCents: number;
  entryCount: number;
  isBillable: boolean;
  groupField?: string;
  subGroups?: SummaryReportGroup[];
}

export interface WeeklyUserRow {
  userId?: string;
  userName: string;
  dailyDurations: Record<string, number>;
  totalDurationSecs: number;
}

export interface WeeklyTableRow {
  id: string;
  projectName: string;
  clientName: string;
  color: string;
  dailyDurations: Record<string, number>;
  totalDurationSecs: number;
  userRows: WeeklyUserRow[];
}

export interface ExportSummary {
  totalDuration: number;
  billableDuration: number;
  amount: number;
}

const DETAILED_COLUMNS = [
  { label: 'Project', value: '' },
  { label: 'Client', value: '' },
  { label: 'Description', value: '' },
  { label: 'Billable', value: '' },
  { label: 'User', value: '' },
  { label: 'Email', value: '' },
  { label: 'Date', value: '' },
  { label: 'Start Date', value: '' },
  { label: 'Start Time', value: '' },
  { label: 'End Date', value: '' },
  { label: 'End Time', value: '' },
  { label: 'Duration', value: '' },
  { label: 'Duration (h)', value: '' },
  { label: 'Amount', value: '' },
];

const mapRowToExportData = (row: DetailedEntry) => [
  { label: 'Project', value: row.projectName || '' },
  { label: 'Client', value: row.clientName || '' },
  { label: 'Description', value: row.description || '' },
  { label: 'Billable', value: row.isBillable ? 'Yes' : 'No' },
  { label: 'User', value: row.userName || '' },
  { label: 'Email', value: row.userEmail || '' },
  { label: 'Date', value: row.date },
  { label: 'Start Date', value: dayjs(row.startedAt).format('YYYY-MM-DD') },
  { label: 'Start Time', value: toHHMM(row.startedAt) },
  {
    label: 'End Date',
    value: row.endedAt ? dayjs(row.endedAt).format('YYYY-MM-DD') : '',
  },
  { label: 'End Time', value: row.endedAt ? toHHMM(row.endedAt) : '' },
  { label: 'Duration', value: formatDuration(row.durationSecs) },
  { label: 'Duration (h)', value: parseFloat((row.durationSecs / 3600).toFixed(2)) },
  { label: 'Amount', value: formatAmount(row.amountCents) },
];

// Detailed report exports
export const handleExportCSV = (
  rows: DetailedEntry[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
) => {
  exportToCSV({
    filename: `ZeLog_Time_Report_Detailed-${startDate}-${endDate}`,
    columns: DETAILED_COLUMNS,
    rows: rows.map(mapRowToExportData),
    // summary: mapSummaryToExportData(summary),
  });
};

export const handleExportExcel = (
  rows: DetailedEntry[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
) => {
  const headers = DETAILED_COLUMNS.map((col) => col.label);
  const dataRows = rows.map((row) => mapRowToExportData(row).map((cell) => cell.value));

  const worksheetData = [headers, ...dataRows];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[addr]) continue;
      if (!worksheet[addr].s) worksheet[addr].s = {};
      worksheet[addr].s.alignment = { horizontal: 'left' };
      if (R === 0) {
        worksheet[addr].s.font = { bold: true };
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Detailed Report');
  XLSX.writeFile(workbook, `ZeLog_Time_Report_Detailed-${startDate}-${endDate}.xlsx`);
};

// Summary report exports

interface FlatRow {
  cells: Array<string | number>;
  isBold: boolean;
}

function flattenSummaryTree(
  rows: SummaryRow[],
  groupFields: string[],
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }>,
  clients: Array<{ id: string; name: string }>,
  users: Array<{ id: string; name: string }>,
): FlatRow[] {
  const numGroupCols = groupFields.length;
  const result: FlatRow[] = [];

  function walkSubGroups(groups: SummaryReportGroup[], depth: number) {
    const sorted = [...groups].sort((a, b) =>
      resolveGroupLabel(a.field, a.key, projects, clients, users).localeCompare(
        resolveGroupLabel(b.field, b.key, projects, clients, users),
      ),
    );
    for (const group of sorted) {
      const label = resolveGroupLabel(group.field, group.key, projects, clients, users);
      const cells: Array<string | number> = Array(numGroupCols).fill('');
      const col = Math.min(depth, numGroupCols - 1);
      if (group.field === 'Project') {
        const project = projects.find((p) => p.id === group.key);
        const clientName = project?.clientId
          ? (clients.find((c) => c.id === project.clientId)?.name ?? '')
          : '';
        cells[col] = clientName ? `${label} \u2014 ${clientName}` : label;
      } else {
        cells[col] = label;
      }
      cells.push(formatDuration(group.totalDurationSecs));
      cells.push(parseFloat((group.totalDurationSecs / 3600).toFixed(2)));
      cells.push(formatAmountValue(group.amountCents));
      const isBold = (group.subGroups?.length ?? 0) > 0;
      result.push({ cells, isBold });
      if (group.subGroups?.length) {
        walkSubGroups(group.subGroups, depth + 1);
      }
    }
  }

  const showClientInProject = groupFields[0] === 'Project';
  const sortedRows = [...rows].sort((a, b) =>
    (a.projectName || a.groupField || '').localeCompare(b.projectName || b.groupField || ''),
  );

  for (const row of sortedRows) {
    const cells: Array<string | number> = Array(numGroupCols).fill('');
    if (showClientInProject) {
      const project = projects.find((p) => p.id === row.projectId);
      const clientName = project?.clientId
        ? (clients.find((c) => c.id === project.clientId)?.name ?? '')
        : '';
      cells[0] = clientName ? `${row.projectName} — ${clientName}` : row.projectName;
    } else {
      cells[0] = row.projectName;
    }
    cells.push(formatDuration(row.totalDurationSecs));
    cells.push(parseFloat((row.totalDurationSecs / 3600).toFixed(2)));
    cells.push(formatAmountValue(row.amountCents));
    const isBold = (row.subGroups?.length ?? 0) > 0;
    result.push({ cells, isBold });
    if (row.subGroups?.length) {
      walkSubGroups(row.subGroups, 1);
    }
  }

  return result;
}
interface DenormalizedRow {
  cells: Array<string | number>;
}

function flattenSummaryTreeDenormalized(
  rows: SummaryRow[],
  groupFields: string[],
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }>,
  clients: Array<{ id: string; name: string }>,
  users: Array<{ id: string; name: string }>,
): DenormalizedRow[] {
  const injectClient = groupFields.includes('Project') && !groupFields.includes('Client');
  const numGroupCols = groupFields.length + (injectClient ? 1 : 0);
  const result: DenormalizedRow[] = [];

  const resolveClientForKey = (key: string | null): string => {
    if (!key) return '';
    const project = projects.find((p) => p.id === key);
    return project?.clientId ? (clients.find((c) => c.id === project.clientId)?.name ?? '') : '';
  };

  function walk(groups: SummaryReportGroup[], ancestors: Array<string | number>) {
    for (const group of groups) {
      const label = resolveGroupLabel(group.field, group.key, projects, clients, users);
      let current = [...ancestors, label];

      // Inject client right after project wherever it appears
      if (injectClient && group.field === 'Project') {
        current = [...ancestors, label, resolveClientForKey(group.key)];
      }

      if (group.subGroups?.length) {
        walk(group.subGroups, current);
      } else {
        const cells: Array<string | number> = [...current];
        while (cells.length < numGroupCols) cells.push('');
        cells.push(formatDuration(group.totalDurationSecs));
        cells.push(parseFloat((group.totalDurationSecs / 3600).toFixed(2)));
        cells.push(formatAmountValue(group.amountCents));
        result.push({ cells });
      }
    }
  }

  for (const row of rows) {
    const rootLabel = row.projectName || row.groupField || '';
    let initialAncestors: Array<string | number>;

    if (injectClient && groupFields[0] === 'Project') {
      const project = projects.find((p) => p.id === row.projectId);
      const clientLabel = project?.clientId
        ? (clients.find((c) => c.id === project.clientId)?.name ?? '')
        : '';
      initialAncestors = [rootLabel, clientLabel];
    } else {
      initialAncestors = [rootLabel];
    }

    if (row.subGroups?.length) {
      walk(row.subGroups, initialAncestors);
    } else {
      const cells: Array<string | number> = [...initialAncestors];
      while (cells.length < numGroupCols) cells.push('');
      cells.push(formatDuration(row.totalDurationSecs));
      cells.push(parseFloat((row.totalDurationSecs / 3600).toFixed(2)));
      cells.push(formatAmountValue(row.amountCents));
      result.push({ cells });
    }
  }

  return result;
}

export const handleSummaryExportCSV = (
  rows: SummaryRow[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
  groupFields: string[] = ['Project'],
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }> = [],
  clients: Array<{ id: string; name: string }> = [],
  users: Array<{ id: string; name: string }> = [],
) => {
  const injectClient = groupFields.includes('Project') && !groupFields.includes('Client');

  // Insert 'Client' right after 'Project' in the column list
  const columnHeaders: string[] = [];
  for (const f of groupFields) {
    columnHeaders.push(f);
    if (injectClient && f === 'Project') columnHeaders.push('Client');
  }
  columnHeaders.push('Time (h)', 'Time (decimal)', 'Amount (USD)');

  const flatRows = flattenSummaryTreeDenormalized(rows, groupFields, projects, clients, users);
  const numGroupCols = columnHeaders.length - 3; // exclude Time (h), Time (decimal), Amount (USD)
  flatRows.sort((a, b) => {
    for (let i = 0; i < numGroupCols; i++) {
      const cmp = String(a.cells[i]).localeCompare(String(b.cells[i]));
      if (cmp !== 0) return cmp;
    }
    return 0;
  });

  const columns = columnHeaders.map((h) => ({ label: h, value: '' }));
  const csvRows = flatRows.map((r) =>
    r.cells.map((cell, i) => ({ label: columnHeaders[i] ?? '', value: String(cell) })),
  );

  exportToCSV({
    filename: `ZeLog_Time_Report_Summary_${startDate}_${endDate}`,
    columns,
    rows: csvRows,
  });
};

export const handleSummaryExportExcel = (
  rows: SummaryRow[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
  groupFields: string[] = ['Project'],
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }> = [],
  clients: Array<{ id: string; name: string }> = [],
  users: Array<{ id: string; name: string }> = [],
) => {
  const columnHeaders = [...groupFields, 'Time (h)', 'Time (decimal)', 'Amount (USD)'];
  const flatRows = flattenSummaryTree(rows, groupFields, projects, clients, users);

  // Style helpers
  const baseStyle = (bold: boolean) => ({
    font: { bold, name: 'Calibri', sz: 10 },
    alignment: { horizontal: 'left' as const, vertical: 'center' as const, wrapText: false },
  });
  const headerStyle = {
    font: { bold: true, name: 'Calibri', sz: 9, color: { rgb: '000000' } },
    // fill: { fgColor: { rgb: '4472C4' } },
    alignment: { horizontal: 'left' as const, vertical: 'center' as const },
  };

  const makeRow = (values: Array<string | number>, bold: boolean) =>
    values.map((v) => ({ v, s: baseStyle(bold) }));

  const worksheetData: any[][] = [
    columnHeaders.map((h) => ({ v: h, s: headerStyle })),
    ...flatRows.map((r) => makeRow(r.cells, r.isBold)),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Fixed column widths: group label columns 35ch wide, time columns 16ch
  worksheet['!cols'] = columnHeaders.map((_, i) =>
    i < groupFields.length ? { wch: 35 } : { wch: 16 },
  );
  // Freeze the header row
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary report');
  XLSX.writeFile(workbook, `ZeLog_Time_Report_Summary_${startDate}_${endDate}.xlsx`);
};

// Weekly report exports

/**
 * Builds a flat list of rows for CSV export (denormalized: one row per user).
 * Columns: Project | Client | User | [date…] | Total
 */
function flattenWeeklyTreeDenormalized(
  rows: WeeklyTableRow[],
  dates: string[],
): Array<Array<string | number>> {
  const result: Array<Array<string | number>> = [];
  for (const row of rows) {
    if (row.userRows.length === 0) {
      // Project with no user breakdown
      const cells: Array<string | number> = [row.projectName, row.clientName, ''];
      for (const d of dates)
        cells.push((row.dailyDurations[d] ?? 0) > 0 ? formatDuration(row.dailyDurations[d]) : '-');
      cells.push(formatDuration(row.totalDurationSecs));
      result.push(cells);
    } else {
      for (const user of row.userRows) {
        const cells: Array<string | number> = [row.projectName, row.clientName, user.userName];
        for (const d of dates)
          cells.push(
            (user.dailyDurations[d] ?? 0) > 0 ? formatDuration(user.dailyDurations[d]) : '-',
          );
        cells.push(formatDuration(user.totalDurationSecs));
        result.push(cells);
      }
    }
  }
  return result;
}

interface WeeklyFlatRow {
  cells: Array<string | number>;
  isBold: boolean;
  isSubRow: boolean;
}

/**
 * Builds a tree-structured flat list for Excel (bold project rows, indented user sub-rows).
 * Columns: Project | User | [date…] | Total
 */
function flattenWeeklyTree(rows: WeeklyTableRow[], dates: string[]): WeeklyFlatRow[] {
  const result: WeeklyFlatRow[] = [];
  for (const row of rows) {
    // Project row (bold): "Project — Client" in Project column, User column empty
    const projCells: Array<string | number> = [
      row.clientName ? `${row.projectName} — ${row.clientName}` : row.projectName,
      '',
    ];
    for (const d of dates)
      projCells.push(
        (row.dailyDurations[d] ?? 0) > 0 ? formatDuration(row.dailyDurations[d]) : '-',
      );
    projCells.push(formatDuration(row.totalDurationSecs));
    result.push({ cells: projCells, isBold: true, isSubRow: false });

    // User sub-rows (normal weight): Project column empty, user name in User column
    for (const user of row.userRows) {
      const userCells: Array<string | number> = ['', user.userName];
      for (const d of dates)
        userCells.push(
          (user.dailyDurations[d] ?? 0) > 0 ? formatDuration(user.dailyDurations[d]) : '-',
        );
      userCells.push(formatDuration(user.totalDurationSecs));
      result.push({ cells: userCells, isBold: false, isSubRow: true });
    }
  }
  return result;
}

export const handleWeeklyExportCSV = (
  rows: WeeklyTableRow[],
  dates: string[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
) => {
  const dateLabels = dates.map((d) => dayjs(d).format('DD/MM/YYYY'));
  const columnHeaders = ['Project', 'Client', 'User', ...dateLabels, 'Total'];
  const flatRows = flattenWeeklyTreeDenormalized(rows, dates);
  // Sort by project then user
  flatRows.sort(
    (a, b) => String(a[0]).localeCompare(String(b[0])) || String(a[2]).localeCompare(String(b[2])),
  );

  exportToCSV({
    filename: `ZeLog_Time_Report_Weekly_${startDate}_${endDate}`,
    columns: columnHeaders.map((h) => ({ label: h, value: '' })),
    rows: flatRows.map((cells) =>
      cells.map((cell, i) => ({ label: columnHeaders[i] ?? '', value: String(cell) })),
    ),
  });
};

export const handleWeeklyExportExcel = (
  rows: WeeklyTableRow[],
  dates: string[],
  summary: ExportSummary,
  startDate: string,
  endDate: string,
) => {
  const dateLabels = dates.map((d) => dayjs(d).format('DD/MM/YYYY'));
  const columnHeaders = ['Project', 'User', ...dateLabels, 'Total'];
  const flatRows = flattenWeeklyTree(rows, dates);

  const baseStyle = (bold: boolean, isSubRow: boolean) => ({
    font: { bold, name: 'Calibri', sz: 10 },
    alignment: {
      horizontal: 'left' as const,
      vertical: 'center' as const,
      indent: isSubRow ? 2 : 0,
    },
  });
  const headerStyle = {
    font: { bold: true, name: 'Calibri', sz: 9, color: { rgb: '000000' } },
    alignment: { horizontal: 'left' as const, vertical: 'center' as const },
  };

  const worksheetData: any[][] = [
    columnHeaders.map((h) => ({ v: h, s: headerStyle })),
    ...flatRows.map((r) => r.cells.map((v) => ({ v, s: baseStyle(r.isBold, r.isSubRow) }))),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  // Project + User cols 30ch wide; date cols 14ch; Total 14ch
  worksheet['!cols'] = columnHeaders.map((_, i) => (i < 2 ? { wch: 30 } : { wch: 14 }));
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Weekly report');
  XLSX.writeFile(workbook, `ZeLog_Time_Report_Weekly_${startDate}_${endDate}.xlsx`);
};

// ─── Summary PDF v2 ──────────────────────

/** Per-date stacked bar data for the PDF chart (mirrors projectChartData from SummaryBarChart). */
export interface ProjectBarChartData {
  /** Ordered date bucket keys (YYYY-MM-DD or similar). */
  buckets: string[];
  /** Map from bucket key → { projectId → seconds }. */
  byDate: Map<string, Record<string, number>>;
  /** All project legend entries in the order they appear in the bars. */
  projects: Array<{ id: string; label: string; color: string }>;
}

export interface SummaryPDFOptions {
  rows: SummaryRow[];
  summary: ExportSummary;
  startDate: string;
  endDate: string;
  groupFields?: string[];
  projects?: Array<{ id: string; name: string; color?: string; clientId?: string }>;
  clients?: Array<{ id: string; name: string }>;
  users?: Array<{ id: string; name: string }>;
  /** Optional date-level stacked bar data for the vertical bar chart. */
  projectBarChartData?: ProjectBarChartData;
  /** Optional base64 PNG logo to show beside the ZeLog brand in the header. */
  logoBase64?: string;
}

// Fallback palette for fields without native colors
const FALLBACK_COLORS = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc948',
  '#b07aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ac',
];

/**
 * Aggregate seconds per unique key for a given group field level.
 */
function aggregateByField(
  rows: SummaryRow[],
  fieldIndex: number,
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }>,
  clients: Array<{ id: string; name: string }>,
  users: Array<{ id: string; name: string }>,
): DonutSliceDef[] {
  const map = new Map<string, { label: string; value: number; color?: string }>();

  function collect(groups: SummaryReportGroup[], depth: number): void {
    for (const g of groups) {
      if (depth === fieldIndex) {
        const label = resolveGroupLabel(g.field, g.key, projects, clients, users);
        const key = g.key ?? label;
        const existing = map.get(key);
        if (existing) {
          existing.value += g.totalDurationSecs;
        } else {
          let color: string | undefined;
          if (g.field === 'Project') {
            const proj = projects.find((p) => p.id === g.key);
            if (proj?.color && proj.color !== '#ccc') color = proj.color;
          }
          map.set(key, { label, value: g.totalDurationSecs, color });
        }
      } else if (g.subGroups?.length) {
        collect(g.subGroups, depth + 1);
      }
    }
  }

  if (fieldIndex === 0) {
    for (const row of rows) {
      const label = row.projectName || row.groupField || '';
      const key = row.id;
      let color: string | undefined;
      if (row.groupField === 'Project' && row.color && row.color !== '#ccc') color = row.color;
      const existing = map.get(key);
      if (existing) {
        existing.value += row.totalDurationSecs;
      } else {
        map.set(key, { label, value: row.totalDurationSecs, color });
      }
    }
  } else {
    for (const row of rows) {
      if (row.subGroups?.length) collect(row.subGroups, 1);
    }
  }

  return Array.from(map.values())
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((s, i) => ({
      label: s.label,
      value: s.value,
      color: s.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));
}

/**
 * Build project distribution bar chart data from summary rows.
 * Returns ordered bucket keys and a Map<bucketKey, BarChartSlice[]>.
 */
function buildProjectBarData(
  rows: SummaryRow[],
  projects: Array<{ id: string; name: string; color?: string; clientId?: string }>,
): { buckets: string[]; data: Map<string, BarChartSlice[]> } {
  // Each top-level row is one "bucket" (the primary grouping label)
  const buckets: string[] = [];
  const data = new Map<string, BarChartSlice[]>();

  for (const row of rows) {
    const label = row.projectName || row.groupField || '';
    buckets.push(label);
    let color = FALLBACK_COLORS[buckets.length % FALLBACK_COLORS.length];
    if (row.groupField === 'Project' && row.color && row.color !== '#ccc') color = row.color;
    data.set(label, [{ label, value: row.totalDurationSecs, color }]);
  }

  return { buckets, data };
}

/**
 * Build a Clockify-style summary PDF.
 * Layout: header → stats → project bar chart → per-field donuts → tree.
 * Tree shows duration only, no percentage.
 * Donuts show: colour dot · label · duration · percentage.
 * Pure addition — does not modify any existing export function.
 */
export function handleSummaryExportPDFv2(opts: SummaryPDFOptions): void {
  const {
    rows,
    summary,
    startDate,
    endDate,
    groupFields = ['Project'],
    projects = [],
    clients = [],
    users = [],
    projectBarChartData,
    logoBase64,
  } = opts;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const dateRange = `${dayjs(startDate).format('DD/MM/YYYY')} – ${dayjs(endDate).format('DD/MM/YYYY')}`;
  let y = drawHeader(doc, 'Summary Report', dateRange, undefined, logoBase64);

  // ── Stats row ───────────────────────────────────────────────────────────────
  y = drawStats(
    doc,
    [
      { label: 'Total:', value: formatDuration(summary.totalDuration) },
      // { label: 'Billable:', value: formatDuration(summary.billableDuration) },
      // { label: 'Amount:', value: formatAmount(summary.amount) },
    ],
    y,
  );

  y += 3;

  // ── Vertical stacked bar chart (date × project) ──────────────────────────────
  if (projectBarChartData && projectBarChartData.buckets.length > 0) {
    // Convert ProjectBarChartData → Map<bucket, BarChartSlice[]>
    const barData = new Map<string, BarChartSlice[]>();
    projectBarChartData.buckets.forEach((bucket) => {
      const byProj = projectBarChartData.byDate.get(bucket) ?? {};
      const slices: BarChartSlice[] = projectBarChartData.projects
        .map((p) => ({ label: p.label, value: byProj[p.id] ?? 0, color: p.color }))
        .filter((s) => s.value > 0);
      barData.set(bucket, slices);
    });
    const legendProjects: BarChartProject[] = projectBarChartData.projects;
    y = drawProjectBarChart(
      doc,
      'Project distribution',
      projectBarChartData.buckets,
      barData,
      formatDuration,
      y,
      legendProjects,
    );
    y += 4;
  } else if (rows.length > 0) {
    // Fallback: project-level totals as horizontal-style single-bar columns
    const { buckets, data } = buildProjectBarData(rows, projects);
    const legendProjects: BarChartProject[] = buckets.map((b, i) => {
      const slices = data.get(b) ?? [];
      return {
        id: b,
        label: b,
        color: slices[0]?.color ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      };
    });
    y = drawProjectBarChart(
      doc,
      'Project distribution',
      buckets,
      data,
      formatDuration,
      y,
      legendProjects,
    );
    y += 4;
  }

  // ── Per-field donut charts (one per active group field, like Clockify) ────────
  for (let i = 0; i < groupFields.length; i++) {
    const field = groupFields[i];
    const slices = aggregateByField(rows, i, projects, clients, users);
    if (!slices.length) continue;

    const totalStr = formatDuration(summary.totalDuration);
    y = drawDonutWithLegend(doc, `${field}`, slices, totalStr, formatDuration, y);
  }

  // ── Section header: column label row ─────────────────────────────────────────
  y += 4;

  // Column headers
  const headerFields = groupFields.join(' / ');
  drawText(doc, headerFields, MARGIN_L, y, {
    size: 7,
    style: 'bold',
    color: [100, 110, 130] as [number, number, number],
  });
  drawTextRight(doc, 'Duration', COL_PCT_X, y, {
    size: 7,
    style: 'bold',
    color: [100, 110, 130] as [number, number, number],
  });
  y += 2;
  drawRule(doc, y, [210, 215, 225], 0.2);
  y += 3;

  // ── Tree walk — duration only ────────────────────────────────────────────────
  function resolveColor(row: SummaryRow): string | undefined {
    if (groupFields[0] === 'Project' && row.color && row.color !== '#ccc') return row.color;
    return undefined;
  }

  function walkSubGroups(groups: SummaryReportGroup[], depth: number): void {
    for (const group of groups) {
      const label = resolveGroupLabel(group.field, group.key, projects, clients, users);
      y = ensureSpace(doc, y, treeRowHeight(depth));
      y = drawTreeRow(
        doc,
        {
          depth,
          label,
          duration: formatDuration(group.totalDurationSecs),
        },
        y,
      );
      if (group.subGroups?.length) walkSubGroups(group.subGroups, depth + 1);
    }
  }

  for (const row of rows) {
    const label = row.projectName || row.groupField || '';
    y = ensureSpace(doc, y, treeRowHeight(0));
    y = drawTreeRow(
      doc,
      {
        depth: 0,
        label,
        duration: formatDuration(row.totalDurationSecs),
        dotColor: resolveColor(row),
      },
      y,
    );
    if (row.subGroups?.length) walkSubGroups(row.subGroups, 1);
  }

  doc.save(`ZeLog_Time_Report_Summary_${startDate}_${endDate}.pdf`);
}

// ─── Detailed PDF v2 ─────────────────────────────────────────────────────────

export interface DetailedPDFOptions {
  rows: DetailedEntry[];
  summary: ExportSummary;
  startDate: string;
  endDate: string;
  /** Optional base64 PNG logo to show beside the ZeLog brand in the header. */
  logoBase64?: string;
}

/**
 * Clockify-style Detailed Report PDF.
 * Layout: header → stats → 4-col table (Date | Description+subtext | Duration | User).
 * Pure addition — does not modify any existing export function.
 */
export function handleDetailedExportPDFv2(opts: DetailedPDFOptions): void {
  const { rows, summary, startDate, endDate, logoBase64 } = opts;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const dateRange = `${dayjs(startDate).format('DD/MM/YYYY')} – ${dayjs(endDate).format('DD/MM/YYYY')}`;
  let y = drawHeader(doc, 'Detailed Report', dateRange, undefined, logoBase64);

  // ── Stats row ─────────────────────────────────────────────────────────────
  y = drawStats(
    doc,
    [
      { label: 'Total:', value: formatDuration(summary.totalDuration) },
      // { label: 'Billable:', value: formatDuration(summary.billableDuration) },
    ],
    y,
  );
  y += 3;

  // ── Column geometry ────────────────────────────────────────────────────────
  // Page content: MARGIN_L(14) … PAGE_W-MARGIN_R(196) = 182mm total
  // Mirrors Clockify proportions:
  //   Date:        14 → 42   (28mm, left-aligned)
  //   Description: 44 → 134  (90mm, left-aligned, wraps)
  //   Duration:   136 → 163  (27mm, left-aligned)
  //   User:       165 → 196  (31mm, left-aligned, bold)
  const DATE_W = 28;
  const DESC_X = MARGIN_L + DATE_W + 2; // 44
  const DESC_MAX_W = 90; // hard cap — long text wraps within this width
  const DUR_X = DESC_X + DESC_MAX_W + 2; // 136 — left-aligned like Clockify
  const USER_X = DUR_X + 29; // 165 — left-aligned, bold

  // Column header row
  const COL_HDR: TextStyle = { size: 7, style: 'bold', color: [100, 110, 130] };
  drawText(doc, 'Date', MARGIN_L, y, COL_HDR);
  drawText(doc, 'Description', DESC_X, y, COL_HDR);
  drawText(doc, 'Duration', DUR_X, y, COL_HDR);
  drawText(doc, 'User', USER_X, y, COL_HDR);
  y += 2;
  drawRule(doc, y, COLOR.line, 0.2);
  y += 2;

  // ── Table rows ─────────────────────────────────────────────────────────────
  const ROW_LINE_H = 4.2; // line height inside a row
  const ROW_PAD_T = 2.5; // space above first text baseline
  const ROW_PAD_B = 2.5; // space below last text baseline before rule
  const SUB_LINE_H = 3.5; // line height for project·client sub-line

  for (const entry of rows) {
    const dateStr = dayjs(entry.date).format('DD/MM/YYYY');
    const description = entry.description || '(Without Description)';
    const subText =
      entry.projectName && entry.clientName
        ? `${entry.projectName} - ${entry.clientName}`
        : entry.projectName || entry.clientName || '';
    const duration = formatDuration(entry.durationSecs);
    const timeRange = entry.endedAt
      ? `${toHHMM(entry.startedAt)} - ${toHHMM(entry.endedAt)}`
      : toHHMM(entry.startedAt);
    const userName = entry.userName || '';

    // Measure wrapped lines for description and user name
    applyStyle(doc, TEXT.l2);
    const descLines: string[] = doc.splitTextToSize(description, DESC_MAX_W);
    const USER_MAX_W = COL_PCT_X - USER_X; // remaining width from user col to page edge
    const userLines: string[] = doc.splitTextToSize(userName, USER_MAX_W);
    const hasSubText = subText.length > 0;

    // Row height: content lines + description sub-line + duration time-range sub-line
    const contentLines = Math.max(descLines.length, userLines.length);
    const rowH =
      ROW_PAD_T +
      contentLines * ROW_LINE_H +
      (hasSubText ? SUB_LINE_H : 0) +
      SUB_LINE_H + // time range sub-line under duration
      ROW_PAD_B;

    y = ensureSpace(doc, y + 1, rowH + 1);

    const baselineY = y + ROW_PAD_T;

    // Date
    drawText(doc, dateStr, MARGIN_L, baselineY, TEXT.l3);

    // Description (wrapped)
    applyStyle(doc, TEXT.l2);
    for (let i = 0; i < descLines.length; i++) {
      doc.text(descLines[i], DESC_X, baselineY + i * ROW_LINE_H);
    }

    // Project · Client sub-line
    if (hasSubText) {
      const subY = baselineY + descLines.length * ROW_LINE_H;
      drawText(doc, subText, DESC_X, subY, TEXT.l4);
    }

    // Duration + time range sub-line
    drawText(doc, duration, DUR_X, baselineY, TEXT.l2);
    drawText(doc, timeRange, DUR_X, baselineY + ROW_LINE_H, TEXT.l4);

    // User name (wrapped)
    applyStyle(doc, TEXT.l2);
    for (let i = 0; i < userLines.length; i++) {
      doc.text(userLines[i], USER_X, baselineY + i * ROW_LINE_H);
    }

    y += rowH - 4;
    drawRule(doc, y, COLOR.line, 0.2);
    y += 1;
  }

  doc.save(`ZeLog_Time_Report_Detailed_${startDate}_${endDate}.pdf`);
}

// ─── Weekly PDF v2 ────────────────────────────────────────────────────────────

export interface WeeklyPDFOptions {
  rows: WeeklyTableRow[];
  dates: string[];
  summary: ExportSummary;
  startDate: string;
  endDate: string;
  logoBase64?: string;
}

/**
 * Clockify-style Weekly Report PDF.
 * Layout: header → stats → hierarchical table (Project rows + indented User sub-rows)
 *         with one column per date and a Total column.
 * Pure addition — does not modify any existing export function.
 */
export function handleWeeklyExportPDFv2(opts: WeeklyPDFOptions): void {
  const { rows, dates, summary, startDate, endDate, logoBase64 } = opts;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const dateRange = `${dayjs(startDate).format('DD/MM/YYYY')} – ${dayjs(endDate).format('DD/MM/YYYY')}`;
  let y = drawHeader(doc, 'Weekly Report', dateRange, undefined, logoBase64);

  // ── Stats ──────────────────────────────────────────────────────────────────
  y = drawStats(
    doc,
    [
      { label: 'Total:', value: formatDuration(summary.totalDuration) },
      // { label: 'Billable:', value: formatDuration(summary.billableDuration) },
    ],
    y,
  );
  y += 3;

  // ── Column geometry ────────────────────────────────────────────────────────
  // Content width = 182mm (MARGIN_L 14 → PAGE_W-MARGIN_R 196)
  // Total col: fixed 22mm, right-aligned at COL_PCT_X (196)
  // Day cols: divide remaining width (182 - 22 = 160mm) minus project col
  // Project col: 45mm fixed; day cols share the rest equally, min 12mm each
  const TOTAL_R = COL_PCT_X; // 196 — Total right edge
  const TOTAL_W = 22;
  const PROJ_W = 45;
  const PROJ_X = MARGIN_L;
  const DAYS_AVAIL = TOTAL_R - TOTAL_W - MARGIN_L - PROJ_W - 2; // space for day cols
  const DAY_W = Math.max(12, Math.floor(DAYS_AVAIL / Math.max(dates.length, 1)));
  const DAY_START_X = PROJ_X + PROJ_W + 2;

  // Right-edge X for each day column (right-aligned values)
  const dayRightX = (i: number) => DAY_START_X + (i + 1) * DAY_W;
  const TOTAL_COL_X = TOTAL_R; // right-align totals here

  const USER_INDENT = 6; // mm indent for user sub-rows

  // Row geometry
  const LINE_H = 4.0; // mm per wrapped line
  const ROW_PAD_TOP = 3.8; // space above first baseline
  const ROW_PAD_BOT = 2.2; // space below last baseline before rule

  const projRowH = (lines: number) => ROW_PAD_TOP + lines * LINE_H;
  const userRowH = (lines: number) => ROW_PAD_TOP + lines * LINE_H;

  // ── Column headers ─────────────────────────────────────────────────────────
  const COL_HDR: TextStyle = { size: 8, style: 'bold', color: [100, 110, 130] };
  drawText(doc, 'Project / User', PROJ_X, y, COL_HDR);
  for (let i = 0; i < dates.length; i++) {
    const label = dayjs(dates[i]).format('ddd D');
    drawTextRight(doc, label, dayRightX(i), y, COL_HDR);
  }
  drawTextRight(doc, 'Total', TOTAL_COL_X, y, COL_HDR);
  y += 2;
  drawRule(doc, y, COLOR.line, 0.2);
  y += 3;

  // ── Rows ───────────────────────────────────────────────────────────────────
  for (const row of rows) {
    // Measure project label lines up front so we know the row height
    const projLabel = row.clientName ? `${row.projectName} — ${row.clientName}` : row.projectName;
    const dotR = 0.6;
    const projMaxW = PROJ_W - dotR * 2 - 3;
    applyStyle(doc, TEXT.l2);
    const projLines: string[] = doc.splitTextToSize(projLabel, projMaxW);
    const pH = projRowH(projLines.length);

    y = ensureSpace(doc, y, pH + 1);
    const projBaseY = y + ROW_PAD_TOP;

    // Color dot
    const hex = row.color?.replace('#', '') || '9e9e9e';
    const dr = parseInt(hex.substring(0, 2), 16);
    const dg = parseInt(hex.substring(2, 4), 16);
    const db = parseInt(hex.substring(4, 6), 16);
    doc.setFillColor(dr, dg, db);
    doc.circle(PROJ_X + dotR, projBaseY - dotR * 0.6, dotR, 'F');

    // Project name (wrapped)
    applyStyle(doc, TEXT.l2);
    for (let i = 0; i < projLines.length; i++) {
      doc.text(projLines[i], PROJ_X + dotR * 2 + 2, projBaseY + i * LINE_H);
    }

    // Day values for project
    for (let i = 0; i < dates.length; i++) {
      const secs = row.dailyDurations[dates[i]] ?? 0;
      const val = secs > 0 ? formatDuration(secs) : '—';
      drawTextRight(doc, val, dayRightX(i), projBaseY, secs > 0 ? TEXT.l3 : TEXT.l4);
    }
    // Project total
    drawTextRight(doc, formatDuration(row.totalDurationSecs), TOTAL_COL_X, projBaseY, TEXT.l2);

    y += pH;
    drawRule(doc, y, COLOR.line, 0.15);
    y += 2;

    // User sub-rows
    for (const userRow of row.userRows) {
      // Measure user name lines up front
      applyStyle(doc, TEXT.l3);
      const userMaxW = PROJ_W - USER_INDENT - 2;
      const userLines: string[] = doc.splitTextToSize(userRow.userName, userMaxW);
      const uH = userRowH(userLines.length);

      y = ensureSpace(doc, y, uH + 1);
      const userBaseY = y + ROW_PAD_TOP;

      // User name (muted, indented, wrapped)
      applyStyle(doc, TEXT.l3);
      for (let i = 0; i < userLines.length; i++) {
        doc.text(userLines[i], PROJ_X + USER_INDENT, userBaseY + i * LINE_H);
      }

      // Day values for user
      for (let i = 0; i < dates.length; i++) {
        const secs = userRow.dailyDurations[dates[i]] ?? 0;
        const val = secs > 0 ? formatDuration(secs) : '—';
        drawTextRight(doc, val, dayRightX(i), userBaseY, TEXT.l4);
      }
      // User total
      drawTextRight(
        doc,
        formatDuration(userRow.totalDurationSecs),
        TOTAL_COL_X,
        userBaseY,
        TEXT.l4,
      );

      y += uH;
      drawRule(doc, y, COLOR.line, 0.15);
      y += 0.5;
    }

    y += 2;
  }

  doc.save(`ZeLog_Time_Report_Weekly_${startDate}_${endDate}.pdf`);
}
